import React from "react";
import { Link } from "react-router-dom";

/* ─────────────────────────────────────────────────────────────────────────
 *  Shared dashboard primitives — used by Student, Company, Institution,
 *  Working-Pro and Admin overview shells so every stakeholder sees a
 *  consistent, professional layout (AmbitionBox-density, SkillConnect brand).
 * ────────────────────────────────────────────────────────────────────── */

/**
 * <KpiTile /> — compact metric card for the top row.
 *
 *   icon           optional lucide icon component
 *   label          eyebrow label (small caps)
 *   value          big number / string
 *   sub            small caption below the value
 *   accent         hex — colours the icon chip and value
 *   to             optional Link target (whole tile becomes clickable)
 *   testid         data-testid override
 *   loading        renders a shimmer skeleton
 */
export function KpiTile({
  icon: Icon,
  label,
  value,
  sub,
  accent = "#006CFF",
  to,
  testid,
  loading,
  cta,
}) {
  const inner = (
    <div className="h-full bg-white border border-rule rounded-xl p-4 transition group-hover:border-signal/40 group-hover:shadow-sm">
      <div className="flex items-center gap-2">
        {Icon && (
          <div
            className="w-8 h-8 rounded-md grid place-items-center shrink-0"
            style={{ background: `${accent}15`, color: accent }}
          >
            <Icon size={16} strokeWidth={2.2} />
          </div>
        )}
        <div className="text-[10px] font-bold uppercase tracking-wider text-muted flex-1 truncate">{label}</div>
      </div>
      <div
        className="font-display font-black text-3xl sm:text-4xl mt-3 leading-none tabular-nums"
        style={{ color: accent }}
        data-testid={testid ? `${testid}-value` : undefined}
      >
        {loading ? <span className="inline-block h-8 w-16 bg-slate-100 rounded animate-pulse align-middle" /> : (value ?? "—")}
      </div>
      {sub && <p className="text-xs text-muted mt-1.5 line-clamp-1">{sub}</p>}
      {cta && (
        <div className="mt-3 text-xs font-semibold text-signal group-hover:underline">
          {cta} →
        </div>
      )}
    </div>
  );
  if (to) {
    return (
      <Link to={to} className="group block h-full" data-testid={testid}>{inner}</Link>
    );
  }
  return <div className="group" data-testid={testid}>{inner}</div>;
}

/**
 * <SectionCard /> — surface wrapper for a labelled section on the overview.
 * Provides eyebrow + optional right-link + consistent padding.
 */
export function SectionCard({
  eyebrow,
  title,
  action,
  children,
  className = "",
  testid,
  pad = true,
}) {
  return (
    <section
      className={`bg-white border border-rule rounded-xl ${pad ? "p-5" : ""} ${className}`}
      data-testid={testid}
    >
      {(eyebrow || title || action) && (
        <header className={`flex items-start justify-between gap-3 ${pad ? "" : "px-5 pt-5"}`}>
          <div className="min-w-0">
            {eyebrow && (
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-signal">{eyebrow}</div>
            )}
            {title && (
              <h3 className="font-display font-bold text-lg mt-1 leading-snug">{title}</h3>
            )}
          </div>
          {action && <div className="shrink-0">{action}</div>}
        </header>
      )}
      <div className={`${pad ? "mt-4" : "p-5 pt-4"}`}>{children}</div>
    </section>
  );
}

/**
 * <DashboardHeader /> — top strip on every dashboard overview.
 * Renders a greeting + CTA row consistent across roles.
 */
export function DashboardHeader({
  eyebrow,
  title,
  subtitle,
  primary,   // { to, label, testid }
  secondary, // { to, label, testid }
  meta,      // React node to render on the right side (e.g. profile completeness)
}) {
  return (
    <div className="bg-white border border-rule rounded-xl p-5 sm:p-6" data-testid="dashboard-header">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="min-w-0">
          {eyebrow && (
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-signal">{eyebrow}</div>
          )}
          <h2 className="font-display font-black tracking-tighter text-2xl sm:text-3xl mt-1 leading-tight">{title}</h2>
          {subtitle && <p className="text-sm text-slate-600 mt-1 max-w-2xl">{subtitle}</p>}
        </div>
        {meta && <div className="shrink-0">{meta}</div>}
      </div>
      {(primary || secondary) && (
        <div className="mt-4 flex gap-2 flex-wrap">
          {primary && (
            <Link
              to={primary.to}
              data-testid={primary.testid}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-semibold text-white shadow-sm hover:shadow-md transition"
              style={{ background: "linear-gradient(135deg, #006CFF 0%, #0040A8 100%)" }}
            >
              {primary.label}
            </Link>
          )}
          {secondary && (
            <Link
              to={secondary.to}
              data-testid={secondary.testid}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-semibold border border-rule text-slate-700 hover:border-signal transition"
            >
              {secondary.label}
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * <CompletenessBar /> — thin progress indicator used on Student + Company overview.
 */
export function CompletenessBar({ percent = 0, label = "Profile completeness" }) {
  const p = Math.max(0, Math.min(100, percent));
  return (
    <div className="min-w-[160px]">
      <div className="text-[10px] font-bold uppercase tracking-wider text-muted flex justify-between">
        <span>{label}</span>
        <span className="text-signal tabular-nums">{p}%</span>
      </div>
      <div className="h-1.5 bg-slate-100 rounded-full mt-1.5 overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${p}%`, background: "linear-gradient(90deg, #006CFF, #0040A8)" }}
        />
      </div>
    </div>
  );
}
